import { motion } from 'framer-motion';
import { useState } from 'react';
import { ChevronDown, MessageCircle, Phone, Mail } from 'lucide-react';
import InsuranceNavbar from '../components/InsuranceNavbar';

const InsuranceFAQ = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqCategories = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "How quickly can HIERSTO be implemented in our insurance company?",
          answer: "Implementation typically takes 15-30 days depending on your package level. Our Essential Partnership can be live in 30 days, Strategic Partnership in 15 days, and Enterprise Partnership offers a 5-day rapid deployment option. We provide dedicated implementation teams to ensure smooth deployment with minimal disruption to your operations."
        },
        {
          question: "What systems does HIERSTO integrate with?",
          answer: "HIERSTO integrates with all major insurance management systems, core banking platforms, EMR systems, and regulatory reporting tools. Our API-first architecture supports both modern and legacy systems. We provide pre-built connectors for popular platforms and custom integration services for specialized systems."
        },
        {
          question: "Do we need to replace our existing systems?",
          answer: "No, HIERSTO is designed to work alongside your existing infrastructure. Our platform acts as an intelligent layer that enhances your current systems without requiring replacement. We focus on augmenting your capabilities rather than disrupting your established workflows."
        },
        {
          question: "What training is provided for our staff?",
          answer: "We provide comprehensive training programs including online modules, hands-on workshops, and certification programs. Training is tailored to different user roles and includes ongoing support. Our Strategic and Enterprise partnerships include dedicated training specialists and regular refresher sessions."
        }
      ]
    },
    {
      category: "Platform Capabilities",
      questions: [
        {
          question: "How accurate is the fraud detection system?",
          answer: "Our AI-powered fraud detection system achieves 98.5% accuracy with less than 0.1% false positives. The system uses machine learning algorithms that continuously improve with your data, identifying patterns that traditional rule-based systems miss. We guarantee fraud detection improvement within 30 days of implementation."
        },
        {
          question: "Can the platform handle our volume of claims?",
          answer: "Yes, HIERSTO is built for enterprise scale. Our platform processes millions of claims monthly for our largest partners. The system auto-scales based on demand and maintains consistent performance regardless of volume. We support anything from 10,000 to over 1 million members with the same reliability."
        },
        {
          question: "What predictive analytics capabilities are included?",
          answer: "Our predictive analytics include risk scoring, claims forecasting, member behavior prediction, cost trend analysis, and churn prediction. The AI models identify high-risk members before expensive claims occur, predict seasonal trends, and recommend proactive interventions to improve outcomes."
        },
        {
          question: "How does the automated claims processing work?",
          answer: "Our intelligent automation handles routine claims end-to-end while routing complex cases to human reviewers. The system validates documents, checks eligibility, applies business rules, and processes payments automatically. Pre-authorization workflows can approve standard requests in under 5 minutes."
        }
      ]
    },
    {
      category: "Security & Compliance",
      questions: [
        {
          question: "Is HIERSTO compliant with UAE healthcare regulations?",
          answer: "Yes, HIERSTO is fully compliant with HAAD, DHA, and all UAE healthcare regulations. We maintain active certifications and our compliance team monitors regulatory changes to ensure continuous compliance. Regular audits and reports are provided to support your regulatory requirements."
        },
        {
          question: "How is our data protected?",
          answer: "We employ enterprise-grade security including end-to-end encryption, multi-factor authentication, role-based access controls, and 24/7 security monitoring. Our platform is ISO 27001 certified, SOC 2 Type II compliant, and hosted in secure, UAE-based data centers with full disaster recovery capabilities."
        },
        {
          question: "Who has access to our member data?",
          answer: "Access to your data is strictly controlled through role-based permissions that you configure. HIERSTO staff have no access to your member data unless explicitly granted for support purposes under strict audit trails. All access is logged and monitored, and we provide detailed access reports."
        },
        {
          question: "What happens to our data if we decide to leave?",
          answer: "You retain full ownership of your data at all times. We provide complete data export capabilities in standard formats, and our contracts include data portability guarantees. There are no data hostage clauses - your data is always accessible to you and can be migrated to other systems if needed."
        }
      ]
    },
    {
      category: "Pricing & ROI",
      questions: [
        {
          question: "How is HIERSTO priced?",
          answer: "We offer three partnership levels: Essential (starting at AED 150K/year), Strategic (starting at AED 300K/year), and Enterprise (custom pricing). Pricing is based on your member count, feature requirements, and support level. We also offer flexible payment terms and volume discounts for multi-year commitments."
        },
        {
          question: "What ROI can we expect?",
          answer: "Our partners typically see 200-400% ROI within the first year. Average savings include 35-45% reduction in claims costs, 70% faster processing times, and significant fraud prevention savings. We provide detailed ROI projections during our consultation and guarantee measurable results within 90 days."
        },
        {
          question: "Are there any hidden costs or additional fees?",
          answer: "No, our pricing is transparent with no hidden fees. The quoted price includes platform access, standard integrations, training, and ongoing support. Optional add-ons like extended member coverage or priority implementation are clearly itemized separately. We believe in straightforward, predictable pricing."
        },
        {
          question: "Do you offer trial periods or proof of concept options?",
          answer: "Yes, we offer a 30-day pilot program where you can test the platform with a subset of your data. This allows you to validate the value proposition before making a full commitment. The pilot includes fraud detection testing, workflow automation, and preliminary ROI analysis."
        }
      ]
    },
    {
      category: "Support & Partnership",
      questions: [
        {
          question: "What ongoing support is provided?",
          answer: "Support levels vary by partnership: Essential includes email/chat support and quarterly reviews, Strategic adds priority phone support and monthly consultations, Enterprise includes 24/7 dedicated support team and weekly strategic sessions. All partnerships include regular platform updates and optimization recommendations."
        },
        {
          question: "How often is the platform updated?",
          answer: "We release platform updates monthly with new features, improvements, and regulatory updates. Security patches are deployed immediately as needed. Major feature releases are planned quarterly, and all partners receive advance notice and training on new capabilities."
        },
        {
          question: "Can HIERSTO grow with our business?",
          answer: "Absolutely. Our platform is designed for scalability. As you add members, expand services, or enter new markets, HIERSTO scales seamlessly. We support multi-entity configurations, different product lines, and various regulatory environments. Our Enterprise partnership includes strategic planning to support your growth objectives."
        },
        {
          question: "What if we need custom features specific to our business?",
          answer: "Our Enterprise partnership includes custom development capabilities. We work with you to understand unique requirements and develop tailored solutions. Our API framework allows for custom integrations and features. We also consider valuable custom features for inclusion in the main platform for all partners."
        }
      ]
    }
  ];

  const contactOptions = [
    {
      title: "Schedule a Call",
      description: "Speak directly with our insurance experts",
      icon: Phone,
      action: "Book Consultation",
      color: "#0077B6"
    },
    {
      title: "Send us a Message",
      description: "Get detailed answers via email",
      icon: Mail,
      action: "Contact Us",
      color: "#03045E"
    },
    {
      title: "Live Chat Support",
      description: "Chat with our team in real-time",
      icon: MessageCircle,
      action: "Start Chat",
      color: "#90E0EF"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <InsuranceNavbar />
      
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-[#90E0EF]/20 border border-[#90E0EF]/30 rounded-full mb-6">
              <MessageCircle className="w-4 h-4 text-[#03045E] mr-2" />
              <span className="text-[#03045E] font-semibold text-sm">Frequently Asked Questions</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#03045E] mb-6">
              Everything You Need to Know About <span className="text-[#0077B6]">HIERSTO</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Find answers to common questions about our insurance transformation platform, 
              implementation process, and partnership opportunities.
            </p>
          </motion.div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
              >
                {/* Category Header */}
                <div className="bg-gradient-to-r from-[#90E0EF]/10 to-[#CAF0F8]/10 px-8 py-6 border-b border-gray-100">
                  <h2 className="text-2xl font-bold text-[#03045E]">{category.category}</h2>
                </div>

                {/* Questions */}
                <div className="divide-y divide-gray-100">
                  {category.questions.map((faq, faqIndex) => {
                    const itemIndex = categoryIndex * 100 + faqIndex;
                    const isOpen = openItems.includes(itemIndex);
                    
                    return (
                      <motion.div
                        key={faqIndex}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: faqIndex * 0.05 }}
                      >
                        <button
                          onClick={() => toggleItem(itemIndex)}
                          className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-[#03045E] pr-4">
                              {faq.question}
                            </h3>
                            <motion.div
                              animate={{ rotate: isOpen ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                              className="flex-shrink-0"
                            >
                              <ChevronDown className="w-5 h-5 text-[#0077B6]" />
                            </motion.div>
                          </div>
                        </button>
                        
                        <motion.div
                          initial={false}
                          animate={{ 
                            height: isOpen ? 'auto' : 0,
                            opacity: isOpen ? 1 : 0
                          }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-8 pb-6">
                            <p className="text-gray-600 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Still Have Questions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-16"
          >
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Our insurance experts are here to help. Get in touch and we'll provide 
                  personalized answers to your specific questions.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {contactOptions.map((option, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 h-full">
                      <div 
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${option.color}20` }}
                      >
                        <option.icon 
                          className="w-8 h-8" 
                          style={{ color: option.color }}
                        />
                      </div>
                      
                      <h3 className="text-xl font-bold text-[#03045E] mb-2">
                        {option.title}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {option.description}
                      </p>
                      
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-300 shadow-md hover:shadow-lg"
                        style={{ backgroundColor: option.color }}
                      >
                        {option.action}
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-12"
          >
            <div className="bg-gradient-to-r from-[#CAF0F8]/30 to-[#90E0EF]/30 rounded-2xl p-8 border border-[#90E0EF]/30">
              <div className="grid md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-[#0077B6] mb-2">24/7</div>
                  <div className="text-sm text-gray-600">Support Available</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0077B6] mb-2">&lt; 2 hrs</div>
                  <div className="text-sm text-gray-600">Average Response Time</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0077B6] mb-2">98%</div>
                  <div className="text-sm text-gray-600">Question Resolution Rate</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0077B6] mb-2">50+</div>
                  <div className="text-sm text-gray-600">Insurance Partners Served</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InsuranceFAQ;
